import { supabase } from '../supabase';

export type LeadFormFieldOption = {
  id: string;
  label: string;
};

export type LeadFormField = {
  id: string;
  type: string;
  label: string;
  mandatory: boolean;
  isCustom?: boolean;
  options?: LeadFormFieldOption[];
  placeholder?: string;
};

export type LeadFormConfig = {
  fields?: LeadFormField[];
  sub_header?: string;
  header_color?: string;
  logo_url?: string;
  background_color?: string;
  text_color?: string;
  button_color?: string;
  show_header?: boolean;
  show_business_name?: boolean;
  show_phone?: boolean;
  show_email?: boolean;
  show_address?: boolean;
  show_direct_contact?: boolean;
  redirect_url?: string;
  button_text?: string;
  updated_at?: string;
};

export type LeadForm = {
  id: string;
  business_id: string;
  title: string;
  description?: string | null;
  form_config?: LeadFormConfig | null;
  /** Public path segment: /{businessSlug}/{public_slug} */
  public_slug?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

/** Path segments that collide with App.tsx public routes under /:businessSlug/… */
export const LEAD_FORM_RESERVED_SLUGS = new Set([
  'book',
  'booking-success',
  'gift-cards',
  'gift-card-success',
  'que',
  'store',
]);

export const LEAD_FORM_COLOR_PALETTE = [
  '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6', '#ffffff',
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d',
  '#16a34a', '#059669', '#0891b2', '#0284c7', '#2563eb', '#4f46e5', '#7c3aed',
  '#9333ea', '#c026d3', '#db2777', '#e11d48',
];

export const DEFAULT_LEAD_FORM_CONFIG: LeadFormConfig = {
  fields: [],
  sub_header: '',
  header_color: '#000000',
  background_color: '#ffffff',
  text_color: '#111827',
  button_color: '#2563eb',
  show_header: true,
  show_business_name: true,
  show_phone: false,
  show_email: false,
  show_address: false,
  show_direct_contact: false,
  redirect_url: '',
  button_text: 'Submit',
};

export function generateFieldId(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Public URL slug from form title (matches PublicLeadForm matching). */
export function leadFormTitleToSlug(title: string): string {
  return normalizeLeadFormSlug(title);
}

/** Normalize a custom URL slug for lead forms. */
export function normalizeLeadFormSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function isReservedLeadFormSlug(slug: string): boolean {
  return LEAD_FORM_RESERVED_SLUGS.has(normalizeLeadFormSlug(slug));
}

/** Effective public path slug: stored public_slug, else title-derived. */
export function getLeadFormPublicSlug(form: {
  title?: string | null;
  public_slug?: string | null;
}): string {
  const custom = normalizeLeadFormSlug(String(form.public_slug || ''));
  if (custom) return custom;
  return leadFormTitleToSlug(String(form.title || ''));
}

export function buildLeadFormPublicUrl(
  origin: string,
  businessSlug: string,
  formTitleOrSlug: string,
): string {
  const slug = normalizeLeadFormSlug(formTitleOrSlug) || leadFormTitleToSlug(formTitleOrSlug);
  return `${origin.replace(/\/$/, '')}/${businessSlug}/${slug}`;
}

export function buildLeadFormEmbedSnippet(publicUrl: string): string {
  const src = publicUrl.includes('?') ? `${publicUrl}&embed=1` : `${publicUrl}?embed=1`;
  return `<iframe src="${src}" title="Lead form" style="width:100%;min-height:720px;border:0;" loading="lazy"></iframe>`;
}

export function buildFormConfig(
  fields: LeadFormField[],
  config: LeadFormConfig | null | undefined,
): LeadFormConfig {
  return {
    fields,
    sub_header: config?.sub_header ?? '',
    header_color: config?.header_color ?? DEFAULT_LEAD_FORM_CONFIG.header_color,
    logo_url: config?.logo_url,
    background_color: config?.background_color ?? DEFAULT_LEAD_FORM_CONFIG.background_color,
    text_color: config?.text_color ?? DEFAULT_LEAD_FORM_CONFIG.text_color,
    button_color: config?.button_color ?? DEFAULT_LEAD_FORM_CONFIG.button_color,
    show_header: config?.show_header ?? true,
    show_business_name: config?.show_business_name ?? true,
    show_phone: config?.show_phone ?? false,
    show_email: config?.show_email ?? false,
    show_address: config?.show_address ?? false,
    show_direct_contact: config?.show_direct_contact ?? false,
    redirect_url: config?.redirect_url ?? '',
    button_text: config?.button_text ?? 'Submit',
    updated_at: new Date().toISOString(),
  };
}

export function createLeadFormField(
  fieldType: string,
  fieldLabel: string,
  isCustom = false,
  options?: LeadFormFieldOption[],
): LeadFormField {
  const baseId = generateFieldId(fieldLabel) || `field_${Date.now()}`;
  return {
    id: `${baseId}_${Date.now().toString(36)}`,
    type: fieldType,
    label: fieldLabel,
    mandatory: false,
    isCustom,
    options,
    placeholder: isCustom ? `Enter ${fieldLabel.toLowerCase()}...` : undefined,
  };
}

export async function fetchLeadForms(businessId: string): Promise<LeadForm[]> {
  if (!supabase) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('lead_forms')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as LeadForm[];
}

export async function fetchLeadFormById(
  businessId: string,
  formId: string,
): Promise<LeadForm | null> {
  if (!supabase) throw new Error('Supabase not available');
  const { data, error } = await supabase
    .from('lead_forms')
    .select('*')
    .eq('business_id', businessId)
    .eq('id', formId)
    .maybeSingle();
  if (error) throw error;
  return (data as LeadForm) || null;
}

export async function createLeadForm(
  businessId: string,
  title: string,
): Promise<LeadForm> {
  if (!supabase) throw new Error('Supabase not available');
  const trimmed = title.trim();
  let publicSlug = normalizeLeadFormSlug(trimmed);
  if (!publicSlug || isReservedLeadFormSlug(publicSlug)) {
    publicSlug = `form-${Date.now().toString(36)}`;
  }
  const { data, error } = await supabase
    .from('lead_forms')
    .insert({
      business_id: businessId,
      title: trimmed,
      public_slug: publicSlug,
      is_active: true,
      form_config: buildFormConfig([], DEFAULT_LEAD_FORM_CONFIG),
    })
    .select()
    .single();
  if (error) throw error;
  return data as LeadForm;
}

export async function updateLeadForm(
  businessId: string,
  formId: string,
  patch: {
    title: string;
    is_active: boolean;
    form_config: LeadFormConfig;
    public_slug?: string | null;
  },
): Promise<void> {
  if (!supabase) throw new Error('Supabase not available');

  const updatePayload: Record<string, unknown> = {
    title: patch.title.trim(),
    is_active: patch.is_active,
    form_config: patch.form_config,
    updated_at: new Date().toISOString(),
  };

  if (patch.public_slug !== undefined) {
    const slug = normalizeLeadFormSlug(String(patch.public_slug || ''));
    if (!slug) throw new Error('Custom URL cannot be empty');
    if (isReservedLeadFormSlug(slug)) {
      throw new Error(`“${slug}” is reserved. Choose a different custom URL.`);
    }
    updatePayload.public_slug = slug;
  }

  const { error } = await supabase
    .from('lead_forms')
    .update(updatePayload)
    .eq('id', formId)
    .eq('business_id', businessId);
  if (error) {
    if ((error as { code?: string }).code === '23505') {
      throw new Error('That custom URL is already used by another form.');
    }
    throw error;
  }
}

export async function deleteLeadForm(
  businessId: string,
  formId: string,
): Promise<void> {
  if (!supabase) throw new Error('Supabase not available');
  const { error } = await supabase
    .from('lead_forms')
    .delete()
    .eq('id', formId)
    .eq('business_id', businessId);
  if (error) throw error;
}
